<template>
    <div v-show="isRendered">
        <br />
        <!-- Metrics -->
        <div class="row align-items-center justify-content-center">
            <div class="col kpi-block">
                <div class="kpi-number-container text-center d-flex">
                    <h1 class="kpi-number kpi-number-integer text-primary">
                        {{ reportData.users_attempted }}
                    </h1>
                </div>
                <p class="text-center medium mb-0">
                    Learners<br />Attempted
                </p>
            </div>
            <div class="col kpi-block">
                <div class="kpi-number-container text-center d-flex">
                    <h1 class="kpi-number kpi-number-integer text-primary">
                        {{ reportData.users_completed }}
                    </h1>
                </div>
                <p class="text-center medium mb-0">
                    Learners<br />Completed
                </p>
            </div>
            <div class="col kpi-block">
                <div class="kpi-number-container text-center d-flex">
                    <h1 class="kpi-number kpi-number-integer text-success">
                        {{ reportData.users_succeeded }}
                    </h1>
                </div>
                <p class="text-center medium mb-0">
                    Learners Successfully
                    <br />Completed
                </p>
            </div>
            <div class="col">
                <div class="kpi-number-container text-center d-flex">
                    <span class="kpi-number text-secondary kpi-number-integer small" style="font-size: 2rem">
                        <span class="">
                            {{ roundScore(reportData.average_score) || "-" }}
                        </span>
                        <span v-show="reportData.case_score_max"> / {{ reportData.case_score_max }} </span>
                    </span>
                </div>
                <p class="text-center medium mb-0">
                    Average
                    <br />Score
                </p>
            </div>
        </div>

        <br />
        <br />
        <br />

        <!-- Export Form -->
        <div class="d-flex justify-content-end mb-2">
            <div class="mb-0">
                <form method="GET" action="/api/reports/completion/export/">
                    <input
                        v-for="param in reportParams"
                        :key="param"
                        hidden
                        style="display: none"
                        :name="param"
                        :value="savedReportQuery[param]"
                    />
                    <button class="btn btn-light btn-sm" type="submit">
                        <span class="icon icon-glyphicons-basic-square-download align-middle mr-1"></span>
                        <span class="align-middle">Export Data</span>
                    </button>
                </form>
            </div>
        </div>

        <!-- Table -->
        <div class="row">
            <div class="col-12">
                <base-table
                    :table-data="reportData.user_report"
                    :config="tableConfig"
                    empty-message="No completion data to display."
                    :rows-per-page="50"
                >
                </base-table>
            </div>
        </div>
    </div>
</template>

<script>
import BaseTable from "@/components/BaseTable"
import http from "@/axios.js"

export default {
    name: "CompletionReport",
    props: {
        libraryItemId: {
            type: String,
            required: false,
        },
        startDate: {
            type: String,
            required: false,
            default: null,
        },
        endDate: {
            type: String,
            required: false,
            default: null,
        },
        shareLinkId: {
            type: Number,
            required: false,
            default: null,
        },
        pageId: {
            type: Number,
            required: false,
            default: null,
        },
        launchRequestId: {
            type: Number,
            required: false,
            default: null,
        },
        includeShareLinks: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    components: {
        BaseTable,
    },
    data() {
        return {
            reportData: {
                user_report: [],
            },
            reportParams: [
                "libraryItemId",
                "startDate",
                "endDate",
                "shareLinkId",
                "pageId",
                "launchRequestId",
                "includeShareLinks",
            ],
            savedReportQuery: {},
            isRendered: false,
        }
    },
    computed: {
        tableConfig() {
            let columns = [
                {
                    name: "Learner",
                    sort: true,
                    key: "user.email",
                    display: (row) => {
                        return row.email || row.identifier || "[Anonymous Learner]"
                    },
                    badges: (row) => {
                        if (row.user_type == "link_user") {
                            return ["Share Link Learner"]
                        }
                    },
                },
                {
                    name: "Attempt Count",
                    sort: true,
                    key: "total_sessions",
                },
                {
                    name: "Completed Attempts",
                    sort: true,
                    key: "complete_sessions",
                },
                {
                    name: "Successful Attempts",
                    sort: true,
                    key: "successful_sessions",
                },
                {
                    name: "Lowest Score",
                    sort: true,
                    key: "lowest_score",
                    display(row) {
                        if (row.lowest_score === null) {
                            return "-"
                        }
                        return String(row.lowest_score)
                    },
                },
                {
                    name: "Highest Score",
                    sort: true,
                    key: "highest_score",
                    display(row) {
                        if (row.highest_score === null) {
                            return "-"
                        }
                        return String(row.highest_score)
                    },
                },
                {
                    name: "Average Score",
                    sort: true,
                    key: "average_score",
                    display(row) {
                        if (row.average_score === null) {
                            return "-"
                        }
                        return Number(row.average_score).toFixed(1)
                    },
                },
            ]
            return {
                initialSort: "Learner",
                initialSortDescend: false,
                multiSelect: false,
                actions: null,
                columns: columns,
            }
        },
    },
    methods: {
        roundScore(score) {
            return Math.round(10 * Number(score)) / 10
        },
        refresh() {
            this.$store.dispatch("addLoad")
            let vm = this

            // Store the currently saved parameters - these are used if the user exports
            // the report currently being viewed.
            this.savedReportQuery = {
                libraryItemId: vm.libraryItemId,
                startDate: vm.startDate,
                endDate: vm.endDate,
                shareLinkId: vm.shareLinkId,
                includeShareLinks: vm.includeShareLinks,
                pageId: vm.pageId,
                launchRequestId: vm.launchRequestId
            }

            // Fetch the report data.
            http.get("/api/reports/completion/", {
                params: this.savedReportQuery,
            })
                .then(function (response) {
                    vm.reportData = response.data
                    vm.isRendered = true
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.kpi-block {
    border-right: 1px solid $gray-300;
    // @media (min-width: 1200px) {
    //   border-right: none
    // }
}

.kpi-block-corner {
    border-right: none;
    @media (min-width: 768px) {
        border-right: 1px solid $gray-300;
    }
}

.kpi-number-container {
    height: 75px;
}

.kpi-number {
    margin: auto;
}

.kpi-number-integer {
    font-size: 2.5rem;
}

.kpi-number {
    font-weight: 300;
}
</style>